import type { ReactNode } from 'react';

// The interface's icons, drawn on a 24 px grid with one light stroke, as FLBI's and fleequid-logs' (Phosphor light).
const PATHS = {
    search: 'M10.5 17a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13zM15.2 15.2 20 20',
    chevron: 'M7 10l5 5 5-5',
    right: 'M10 7l5 5-5 5',
    x: 'M6.5 6.5l11 11M17.5 6.5l-11 11',
    plus: 'M12 5v14M5 12h14',
    check: 'M5 12.5l4.5 4.5L19 7.5',
    dots: 'M6 12h.01M12 12h.01M18 12h.01',
    eye: 'M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12zM12 14.8a2.8 2.8 0 1 0 0-5.6 2.8 2.8 0 0 0 0 5.6z',
    eyeoff: 'M3.5 3.5l17 17M10.4 6.1A9.4 9.4 0 0 1 12 5.5c6 0 9.5 6.5 9.5 6.5a17.6 17.6 0 0 1-3.1 3.9M6.7 6.8C4 8.4 2.5 12 2.5 12S6 18.5 12 18.5c1.7 0 3.2-.5 4.5-1.2M10 10a2.8 2.8 0 0 0 4 4',
    copy: 'M11.5 9h7A2.5 2.5 0 0 1 21 11.5v7a2.5 2.5 0 0 1-2.5 2.5h-7A2.5 2.5 0 0 1 9 18.5v-7A2.5 2.5 0 0 1 11.5 9zM5 15V5.5A2.5 2.5 0 0 1 7.5 3H15',
    history: 'M3.5 12a8.5 8.5 0 1 0 2.5-6M3.5 4.5v4h4M12 7.5V12l3 2',
    pencil: 'M14.5 5.5l4 4M4.5 19.5l1-4.5L15.3 5.2a1.4 1.4 0 0 1 2 0l1.5 1.5a1.4 1.4 0 0 1 0 2L9 18.5z',
    trash: 'M4.5 7h15M10 11v6M14 11v6M6 7l1 12.5A1.5 1.5 0 0 0 8.5 21h7a1.5 1.5 0 0 0 1.5-1.5L18 7M9 7V4.5h6V7',
    undo: 'M9 14L4 9l5-5M4 9h10.5a5.5 5.5 0 0 1 0 11H11',
    download: 'M12 4v11M7 10.5l5 5 5-5M5 19.5h14',
    upload: 'M12 15.5V4.5M7 9l5-5 5 5M5 19.5h14',
    lock: 'M7.5 10.5V8a4.5 4.5 0 0 1 9 0v2.5M6.5 10.5h11a1 1 0 0 1 1 1V19a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1v-7.5a1 1 0 0 1 1-1zM12 14.2v2.3',
    key: 'M15 13a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM12.2 11.8 4 20M7 17l2 2M9.6 14.4l2 2',
    columns: 'M4.5 5h15v14h-15zM9.5 5v14M14.5 5v14',
    pulse: 'M3 12h4l2.5-6 5 12 2.5-6h4',
    users: 'M9 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM2.5 19.5c1-3 3.4-4.5 6.5-4.5s5.5 1.5 6.5 4.5M16 4.5a3.5 3.5 0 0 1 0 6.5M18 15c2 .6 3.2 2.2 3.5 4.5',
    layers: 'M12 3.5l8.5 4.5-8.5 4.5L3.5 8zM3.5 12l8.5 4.5 8.5-4.5M3.5 16l8.5 4.5 8.5-4.5',
    warning: 'M12 4.5l8.5 15h-17zM12 10v4M12 17h.01',
    file: 'M14 3.5H7A1.5 1.5 0 0 0 5.5 5v14A1.5 1.5 0 0 0 7 20.5h10a1.5 1.5 0 0 0 1.5-1.5V8zM14 3.5V8h4.5',
    logout: 'M14 4.5h4A1.5 1.5 0 0 1 19.5 6v12a1.5 1.5 0 0 1-1.5 1.5h-4M10 16l4-4-4-4M14 12H4.5',
    keyboard: 'M3.5 6.5h17v11h-17zM7 10h.01M10 10h.01M13 10h.01M16 10h.01M8 14h8',
    swap: 'M7 4.5v13M4 14.5l3 3 3-3M17 19.5v-13M14 9.5l3-3 3 3',
    up: 'M12 19V5M6 11l6-6 6 6',
    down: 'M12 5v14M6 13l6 6 6-6',
    arrow: 'M5 12h14M13 6l6 6-6 6',
    shield: 'M12 3.5l7 2.8v5.4c0 4.3-3 7.6-7 8.8-4-1.2-7-4.5-7-8.8V6.3zM9 12l2 2 4-4',
} as const;

export type IconName = keyof typeof PATHS;

export function Icon(props: { name: IconName; size?: number; className?: string }): ReactNode {
    const size = props.size ?? 16;

    return (
        <svg
            className={props.className}
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={props.name === 'dots' ? 2.6 : 1.6}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
        >
            <path d={PATHS[props.name]} />
        </svg>
    );
}
